import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaPen, FaTrash, FaCheck, FaTimes, FaXRay } from "react-icons/fa";
import PageHeading from "../../PageHeading";
import TableSkeleton from "../../TableSkeleton";
import ExportOptions from "../../ExportOptions";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const XRAY_URL = "https://api.muktihospital.com/api/nicu/xray";

/**
 * X-ray types are the price list every X-ray slip is built from.
 * A type that has already been used is switched off instead of deleted,
 * so old slips keep showing the name they were created with.
 */
const NicuXrayType = () => {
  const [types, setTypes] = useState([]);
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({ name: "", price: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", price: "" });

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${XRAY_URL}/type`, {
        headers: { "x-api-key": API_KEY },
        params: { includeInactive: "true" },
      });
      const list = res.data.types || [];
      setTypes(list);

      // Cache the X-ray type list for offline viewing.
      setNicuOfflineActive(false);
      saveToCache("nicu_xray_types_list", list);
    } catch (err) {
      console.error("Error loading X-ray types:", err);
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache("nicu_xray_types_list");
        if (cached && Array.isArray(cached)) {
          setTypes(cached);
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved X-ray list.");
        } else {
          setTypes([]);
          setNicuOfflineActive(false);
          toast.info("Offline: open this page online once to save it.");
        }
      } else {
        toast.error("Failed to load the X-ray list.");
        setTypes([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const handleAdd = async () => {
    if (!form.name.trim()) {
      toast.error("Enter the X-ray name.");
      return;
    }
    if (!form.price || parseFloat(form.price) <= 0) {
      toast.error("Enter a price greater than 0.");
      return;
    }

    try {
      setSaving(true);
      await axios.post(
        `${XRAY_URL}/type/add`,
        { name: form.name.trim(), price: parseFloat(form.price) },
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success(`"${form.name.trim()}" added.`);
      setForm({ name: "", price: "" });
      fetchTypes();
    } catch (err) {
      console.error("Failed to add X-ray type:", err);
      toast.error(err.response?.data?.error || "Failed to add the X-ray.");
    } finally {
      setSaving(false);
    }
  };

  // Enter anywhere in the add form saves it, so the whole list can be typed in one go
  const handleAddKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const startEdit = (type) => {
    setEditingId(type.id);
    setEditForm({ name: type.name, price: String(type.price) });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: "", price: "" });
  };

  const saveEdit = async (id) => {
    if (!editForm.name.trim()) {
      toast.error("Name cannot be empty.");
      return;
    }
    if (!editForm.price || parseFloat(editForm.price) <= 0) {
      toast.error("Enter a price greater than 0.");
      return;
    }

    try {
      setSaving(true);
      await axios.put(
        `${XRAY_URL}/type/edit/${id}`,
        { name: editForm.name.trim(), price: parseFloat(editForm.price) },
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("X-ray updated.");
      cancelEdit();
      fetchTypes();
    } catch (err) {
      console.error("Failed to update X-ray type:", err);
      toast.error(err.response?.data?.error || "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (type) => {
    try {
      await axios.put(
        `${XRAY_URL}/type/edit/${type.id}`,
        { isActive: !type.isActive },
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success(type.isActive ? `"${type.name}" switched off.` : `"${type.name}" switched on.`);
      fetchTypes();
    } catch (err) {
      console.error("Failed to change status:", err);
      toast.error("Failed to change the status.");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${XRAY_URL}/type/delete/${deleteTarget.id}`, {
        headers: { "x-api-key": API_KEY },
      });
      toast.success("X-ray removed from the list.");
      setDeleteTarget(null);
      fetchTypes();
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(err.response?.data?.error || "Failed to remove.");
      setDeleteTarget(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return types.filter((t) => (t.name || "").toLowerCase().includes(q));
  }, [types, searchQuery]);

  const activeCount = types.filter((t) => t.isActive).length;

  const headers = ["X-ray Name", "Price", "Status"];
  const dataMapper = (t) => [t.name, t.price, t.isActive ? "Active" : "Inactive"].join("\t");
  const breadcrumbs = useMemo(
    () => [{ label: "Dashboard", url: "/" }, { label: "NICU" }, { label: "X-ray List" }],
    []
  );

  const roundButton = "size-7 rounded-full text-sm inline-flex items-center justify-center transition-all duration-300";

  return (
    <div>
      <ToastContainer />
      <PageHeading title="NICU X-ray List" breadcrumbs={breadcrumbs} />
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-M-primary-color">
          <p className="text-xs text-gray-500">Total X-rays</p>
          <p className="text-xl font-black text-M-primary-color">{types.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-[#73CB64]">
          <p className="text-xs text-gray-500">Active</p>
          <p className="text-xl font-black text-[#73CB64]">{activeCount}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-gray-400">
          <p className="text-xs text-gray-500">Switched Off</p>
          <p className="text-xl font-black text-gray-600">{types.length - activeCount}</p>
        </div>
      </div>

      {/* Add form */}
      <div className="bg-white shadow-sm rounded-lg mb-4 border-l-4 border-[#73CB64]">
        <div className="p-4 border-b border-dashed border-M-text-color/50 flex items-center gap-2">
          <FaXRay className="text-[#73CB64]" />
          <h2 className="text-base font-medium text-gray-700">Add an X-ray</h2>
        </div>
        <div className="p-4" onKeyDown={handleAddKeyDown}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs font-bold block mb-1">X-ray Name *</label>
              <input
                type="text"
                placeholder="e.g. Chest X-ray AP"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Price (৳) *</label>
              <input
                type="number"
                placeholder="0"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none"
                min="0"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Press Enter to save and keep typing the next one.
          </p>
          <div className="flex justify-end mt-3">
            <button
              onClick={handleAdd}
              disabled={saving}
              className="bg-[#73CB64] text-white text-sm py-2 px-5 rounded-md hover:bg-M-primary-color flex items-center gap-1.5 disabled:opacity-50 transition-all duration-300"
            >
              <FaPlus size={12} /> {saving ? "Saving..." : "Add X-ray"}
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
          <h2 className="text-base font-medium text-gray-700">X-ray Price List</h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <input
              type="text"
              placeholder="Search X-ray name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-48 w-full p-2 border rounded-md text-sm outline-none"
            />
            <ExportOptions
              data={filtered}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
              title="NICU X-ray Price List"
              headers={headers}
              dataMapper={dataMapper}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <table className="min-w-[700px] w-full">
              <TableSkeleton
                rowCount={6}
                columns={[{ width: "60px" }, { width: "260px" }, { width: "100px" }, { width: "100px" }, { width: "110px" }]}
              />
            </table>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              {searchQuery ? "No X-ray matches that search." : "No X-ray added yet. Add the first one above."}
            </div>
          ) : (
            <table className="min-w-[700px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">SL</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">X-ray Name</th>
                  <th className="p-3 text-right text-sm font-medium text-gray-700">Price (৳)</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700">Status</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, index) => {
                  const isEditing = editingId === t.id;
                  return (
                    <tr key={t.id} className={`border-b hover:bg-gray-50 ${isEditing ? "bg-blue-50" : ""}`}>
                      <td className="p-3 text-sm text-gray-500">{index + 1}</td>

                      <td className="p-3 text-sm text-gray-700">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="w-full border p-1.5 rounded text-sm focus:border-M-primary-color outline-none"
                          />
                        ) : (
                          <span className={t.isActive ? "" : "text-gray-400 line-through"}>{t.name}</span>
                        )}
                      </td>

                      <td className="p-3 text-sm text-right font-bold text-M-primary-color">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.price}
                            onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                            className="w-24 border p-1.5 rounded text-sm text-right focus:border-M-primary-color outline-none"
                            min="0"
                          />
                        ) : (
                          `৳${t.price}`
                        )}
                      </td>

                      <td className="p-3 text-center">
                        <button
                          onClick={() => toggleActive(t)}
                          disabled={isEditing}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-300 ${
                            t.isActive
                              ? "bg-green-50 text-green-700 hover:bg-green-100"
                              : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                          } ${isEditing ? "opacity-50 cursor-not-allowed" : ""}`}
                          title={t.isActive ? "Click to switch off" : "Click to switch on"}
                        >
                          {t.isActive ? "Active" : "Inactive"}
                        </button>
                      </td>

                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => saveEdit(t.id)}
                                disabled={saving}
                                className={`${roundButton} bg-[#73CB64]/10 text-[#73CB64] hover:bg-[#73CB64] hover:text-white`}
                                title="Save"
                              >
                                <FaCheck size={12} />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className={`${roundButton} bg-gray-100 text-gray-500 hover:bg-gray-400 hover:text-white`}
                                title="Cancel"
                              >
                                <FaTimes size={12} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => startEdit(t)}
                                className={`${roundButton} bg-M-Green-color/10 text-M-Green-color hover:bg-M-Green-color hover:text-white`}
                                title="Edit name and price"
                              >
                                <FaPen size={12} />
                              </button>
                              <button
                                onClick={() => setDeleteTarget(t)}
                                className={`${roundButton} bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white`}
                                title="Remove from the list"
                              >
                                <FaTrash size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
            <h3 className="text-base font-bold text-gray-700 mb-2">
              Remove &quot;{deleteTarget.name}&quot;?
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Old slips keep the name they were made with. If this X-ray is still being done sometimes,
              switch it off instead — that keeps it out of the dropdown without removing it.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="bg-gray-200 text-gray-700 text-sm py-2 px-4 rounded-md hover:bg-gray-300 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="bg-M-secondary-color text-white text-sm py-2 px-4 rounded-md hover:bg-red-700 transition-all duration-300"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NicuXrayType;
